/**
 * lastUpdated — the "last updated" render logic for a portal report
 * (SPEC-stakeholder-portal-ux-hardening-2026-07-08, R5 AC3).
 *
 * A report's `last_updated` (an ISO timestamp from the backend) is rendered as a
 * PAST-ONLY, DATE-FREE relative string. Two shapes:
 *
 *   • fresh  → "Updated 3 hours ago"  (relativePast)
 *   • stale  → "No change in 12 days, still on our list."  (honest reassurance)
 *
 * The stale conversion is the honest-work case: when nothing has changed for a
 * while, a bare "updated N ago" can read as neglect, so past STALE_DAYS it flips
 * to a reassurance that the report is still tracked — WITHOUT promising a date or
 * a fix (no future/target date is ever produced here). A future timestamp is
 * clamped to "just now" so the surface never renders a future time.
 *
 * Pure functions (no I/O); `now` is injectable so tests are deterministic.
 */

// Past this many days with no change, "updated N ago" flips to the reassurance.
export const STALE_DAYS = 7;

const MS_PER_DAY = 86_400_000;

/** A PAST-ONLY plain-language relative string. A value at/after `now` clamps to
 *  "just now" — never a future time, never an absolute date. Returns null for a
 *  missing/unparseable timestamp so callers can render nothing. */
export function relativePast(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const secs = Math.max(0, (now - then) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`;
  const hours = Math.floor(secs / 3600);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(secs / 86400);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  if (days < 30) {
    const weeks = Math.floor(days / 7);
    return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  }
  const months = Math.floor(days / 30);
  return `${months} month${months === 1 ? '' : 's'} ago`;
}

/** The rendered "last updated" line for a report row (R5 AC3). Fresh rows read
 *  "Updated <n> ago"; a row unchanged for STALE_DAYS+ flips to the date-free,
 *  commitment-free reassurance. Returns null when there is no timestamp. */
export function lastUpdatedLabel(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  const then = Date.parse(iso);
  if (Number.isNaN(then)) return null;
  const days = Math.floor(Math.max(0, now - then) / MS_PER_DAY);
  if (days >= STALE_DAYS) {
    return `No change in ${days} days, still on our list.`;
  }
  const rel = relativePast(iso, now);
  return rel ? `Updated ${rel}` : null;
}
